import { Button, Form, Input, message, Select, DatePicker, Row, Col } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "../../server";
import { CloseOutlined } from "@ant-design/icons";
import { orderStatuses } from "../../extensions/helper";
import { strDate2Server } from "../../extensions/utils";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { isMobile } from "@/extensions/design";
import ButtonReport from "@/controls/button-report";

const LicenseOrder = ({ id, onClose }) => {
  const [item, setItem] = useState({ status: 0, docDate: dayjs() });
  const [loading, setLoading] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  const [tariffsData, setTariffsData] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [form] = Form.useForm();
  const user = useSelector((s) => s.user);

  const isSuperAdmin = user.isSuperadmin;
  const isNewOrder = id === 0;
  const tariffFoundInList = !item.tariffId || tariffsData.some(t => t.id === item.tariffId);

  const getData = () => {
    if (id) {
      setLoading(true);
      serverFetch(`Order/${id}`)
        .then(data => {
          setLoading(false);
          const formData = {
            ...data,
            docDate: data.docDate ? dayjs(data.docDate) : dayjs(),
            activated: data.activated ? dayjs(data.activated) : null,
            finished: data.finished ? dayjs(data.finished) : null,
            paidDeadline: data.paidDeadline ? dayjs(data.paidDeadline) : null,
          };
          setItem(data);
          setCompanyName(data.companyName || "");
          form.setFieldsValue(formData);
        })
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        });
    } else {
      // Для нового заказа загружаем название компании пользователя
      if (user.companyId) {
        serverFetch(`company/${user.companyId}`)
          .then(company => {
            setCompanyName(company.name || "");
            const defaultFormData = {
              docDate: dayjs(),
              status: 0,
              authorName: user.userName,
              companyId: user.companyId,
            };
            form.setFieldsValue(defaultFormData);
            setItem({ ...item, ...defaultFormData });
          })
          .catch(e => message.error(e.userMessage));
      }
    }
  };

  useEffect(() => {
    getData();
    // Загружаем список тарифов (активные, без флага default)
    serverFetch("Tariff")
      .then(data => {
        const activeTariffs = data.filter(t => t.active && !t.default);
        setTariffsData(activeTariffs);
        setTariffs(activeTariffs.map(t => ({ label: t.name, value: t.id })));
        
        // Для нового заказа устанавливаем первый доступный тариф
        if (!id && activeTariffs.length > 0) {
          const firstTariff = activeTariffs[0];
          form.setFieldsValue({ tariffId: firstTariff.id, price: firstTariff.price });
          setItem(prev => ({ ...prev, tariffId: firstTariff.id, price: firstTariff.price }));
        }
      })
      .catch(e => message.error(e.userMessage));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onValuesChange = (changedValues, allValues) => {
    // Если изменился тариф, автоматически обновляем цену
    if (changedValues.tariffId) {
      const selectedTariff = tariffsData.find(t => t.id === changedValues.tariffId);
      if (selectedTariff) {
        form.setFieldsValue({ price: selectedTariff.price });
        setItem({ ...item, ...changedValues, price: selectedTariff.price });
        return;
      }
    }
    setItem({ ...item, ...changedValues });
  };

  const onSave = () => {
    form.validateFields().then(() => {
      setLoading(true);
      
      const saveData = {
        ...item,
        docDate: strDate2Server(item.docDate),
        activated: strDate2Server(item.activated),
        finished: strDate2Server(item.finished),
        paidDeadline: item.paidDeadline ? (dayjs.isDayjs(item.paidDeadline) ? item.paidDeadline.toISOString() : item.paidDeadline) : null,
      };

      serverFetch("Order", { method: id ? "PUT" : "POST", bodyData: saveData })
        .then(() => onClose(true))
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        });
    });
  };

  return !isMobile ? (
    <div className="sport-modal">
      <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose(false)}>
        <div className="sport-modal-content" style={{ width: 600 }} onMouseDown={e => e.stopPropagation()}>
          <div className="sport-section-title">
            <span>{id ? "Заказ" : "Новый заказ"}</span>
            <CloseOutlined className="sport-section-title-close" onClick={() => onClose(false)} />
          </div>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={onValuesChange}
          >
            {!isNewOrder && (
              <Form.Item name="id" label="#">
                <Input disabled />
              </Form.Item>
            )}

            <Form.Item label="Компания">
              <Input value={companyName} disabled />
            </Form.Item>

            <Form.Item
              name="docDate"
              label="Дата"
            >
              <DatePicker 
                format="DD.MM.YYYY" 
                disabled={!isSuperAdmin}
                style={{ width: '100%' }}
              />
            </Form.Item>

            {tariffFoundInList ? (
              <Form.Item
                name="tariffId"
                label="Тариф"
                rules={[{ required: true, message: "Выберите тариф" }]}
              >
                <Select 
                  options={tariffs} 
                  placeholder="Выберите тариф"
                />
              </Form.Item>
            ) : (
              <Form.Item label="Тариф">
                <Input disabled value={item.tariffName ?? ''} />
              </Form.Item>
            )}

            <Form.Item name="status" label="Статус">
              <Select 
                options={orderStatuses} 
                disabled={!isSuperAdmin}
              />
            </Form.Item>

            <Form.Item name="price" label="Цена">
              <Input type="number" disabled />
            </Form.Item>

            {!isNewOrder && (
              <Form.Item name="authorName" label="Автор">
                <Input disabled />
              </Form.Item>
            )}

            <Form.Item name="activated" label="Активация">
              <DatePicker 
                format="DD.MM.YYYY" 
                disabled={!isSuperAdmin}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="finished" label="Окончание">
              <DatePicker 
                format="DD.MM.YYYY" 
                disabled={!isSuperAdmin}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="paidDeadline" label="Срок оплаты">
              <DatePicker 
                format="DD.MM.YYYY HH:mm" 
                showTime 
                disabled={!isSuperAdmin}
                style={{ width: '100%' }}
              />
            </Form.Item>

            {!isNewOrder && (
              <>
                <Form.Item name="receiptUrl" label="URL чека">
                  <Input disabled />
                </Form.Item>

                <Form.Item name="paymentId" label="ID платежа">
                  <Input disabled />
                </Form.Item>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button onClick={() => onClose(false)}>Отмена</Button>
              {!isNewOrder && <ButtonReport vid={9} id={id} />}
              <Button type="primary" onClick={onSave} loading={loading}>
                Сохранить
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  ) : (
  <>
  <div className="sport-modal">
      <div className="sport-modal-overlay">
        <div className="sport-modal-content">
          <div className="sport-modal-title" style={{marginBottom: "0px"}}>
            <span>{id ? "Заказ" : "Новый заказ"}</span>
            <CloseOutlined onClick={() => onClose(false)} className="sport-section-title-close client-close-outlined-button-style"/>
          </div>
          <Form form={form} layout="inline" onValuesChange={onValuesChange}>
            {!isNewOrder && (
              <Form.Item name="id" label="#" className="record-form-item-with-margin-top-mobile">
                <Input disabled className="records-form-item-mobile"/>
              </Form.Item>
            )}

            <Form.Item label="Компания" className="record-form-item-with-margin-top-mobile"> 
              <Input value={companyName} disabled className="records-form-item-mobile"/>
            </Form.Item>

            <Form.Item name="docDate" label="Дата" className="record-form-item-with-margin-top-mobile">
              <DatePicker format="DD.MM.YYYY" disabled={!isSuperAdmin} className="records-form-item-mobile"/>
            </Form.Item>

            {tariffFoundInList ? (
              <Form.Item name="tariffId" label="Тариф" rules={[{ required: true, message: "Выберите тариф" }]} className="record-form-item-with-margin-top-mobile">
                <Select options={tariffs} placeholder="Выберите тариф" className="records-form-item-mobile"/>
              </Form.Item>
            ) : (
              <Form.Item label="Тариф" className="record-form-item-with-margin-top-mobile">
                <Input disabled value={item.tariffName ?? ''} className="records-form-item-mobile"/>
              </Form.Item>
            )}

            <Form.Item name="status" label="Статус" className="record-form-item-with-margin-top-mobile">
              <Select options={orderStatuses} disabled={!isSuperAdmin} className="records-form-item-mobile"/>
            </Form.Item>

            <Form.Item name="price" label="Цена" className="record-form-item-with-margin-top-mobile">
              <Input type="number" disabled className="records-form-item-mobile"/>
            </Form.Item>

            {!isNewOrder && (
              <Form.Item name="authorName" label="Автор" className="record-form-item-with-margin-top-mobile">
                <Input disabled className="records-form-item-mobile"/>
              </Form.Item>
            )}

            <Form.Item className="records-form-item-mobile">
              <Row align="middle" className="records-row-mobile">
                <Col>
                  <Form.Item label="Актив." name="activated" labelCol={{ span: 4 }} className="record-form-item-with-margin-top-mobile">
                    <DatePicker disabled={!isSuperAdmin} format="DD.MM.YYYY" className="sales-small-input-mobile"/>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label="Оконч." labelCol={{ span: 2 }} wrapperCol={{ span: 6}} name="finished" className="record-form-item-with-margin-top-mobile">
                    <DatePicker disabled={!isSuperAdmin} format="DD.MM.YYYY" className="licenses-small-input-mobile"/>
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item name="paidDeadline" label="Срок опл." className="record-form-item-with-margin-top-mobile">
              <DatePicker format="DD.MM.YYYY HH:mm" showTime disabled={!isSuperAdmin} className="records-form-item-mobile"/>
            </Form.Item>

            {!isNewOrder && (
              <>
                <Form.Item name="receiptUrl" label="URL чека" className="record-form-item-with-margin-top-mobile">
                  <Input disabled className="records-form-item-mobile"/>
                </Form.Item>

                <Form.Item name="paymentId" label="ID платежа" className="record-form-item-with-margin-top-mobile">
                  <Input disabled className="records-form-item-mobile"/>
                </Form.Item>
              </>
            )}
              {!isNewOrder && <ButtonReport vid={9} id={id} />}
              <Button type="primary" onClick={onSave} loading={loading} className="records-form-item-mobile" style={{marginTop: "4px"}}> Сохранить </Button>
          </Form>
        </div>
      </div>
    </div>
  </>);
};

export default LicenseOrder;

